import React, { useState } from 'react';
import PropTypes from 'prop-types';
import reduce from 'lodash/reduce';
import isEqual from 'lodash/isEqual';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import get from 'lodash/get';
import find from 'lodash/find';

import BaseModal from 'pages/MyAccount/shared-components/BaseModal/Modal';
import Button, { BUTTON_TYPES } from 'pages/MyAccount/shared-components/Button/Button';
import CloseButton from 'pages/MyAccount/shared-components/CloseButton/CloseButton';
import { COUNTRY_SELECTION } from 'pages/MyAccount/utils';

import AddressOption from '../AddressOption/AddressOption';
import styles from './VerificationModal.module.css';

const getSuggestedFields = (submitted, verified) =>
  reduce(
    verified,
    (result, value, key) => {
      return isEqual(value, submitted[key]) ? result : [...result, key];
    },
    []
  );
const formatData = ({
  primaryLine,
  secondaryLine,
  city,
  state,
  zip,
  countryCode,
}) => ({
  shipAddress1: primaryLine,
  shipAddress2: secondaryLine,
  shipCity: city,
  shipState: state,
  shipZipCode: zip,
  shipCountry: countryCode,
});
const formatAddress = (value) => startCase(toLower(value));

const VerificationModal = ({
  modal,
  data,
  loading,
  closeModal,
  onClickUseThisAddress,
  onClickUseAsEntered,
}) => {
  const { submitted, verified } = data;
  const [suggestedSelected, setSuggestedSelected] = useState(true);
  const toggleSelected = () => setSuggestedSelected((prevState) => !prevState);
  const suggestedFields = getSuggestedFields(submitted, verified);

  const resetState = () => setSuggestedSelected(true);
  const handleClickClose = () => {
    resetState();
    closeModal();
  };
  const handleClickUseThisAddress = () => {
    if (suggestedSelected) {
      const suggestedData = formatData(verified);
      onClickUseThisAddress(suggestedSelected, suggestedData, resetState);
    } else {
      onClickUseThisAddress(suggestedSelected, {}, resetState);
    }
  };

  return (
    <BaseModal
      isOpen={!!modal}
      showCloseIcon
      onRequestClose={handleClickClose}
      className={styles.modal}
    >
      <div className={styles.container}>
        <CloseButton onClick={handleClickClose} />
        <div className={styles.content}>
          {modal === 'suggested' && (
            <>
              <h1 className={styles.title}>
                Oops! 
                {' '}
                <span>Please Verify</span>
              </h1>
              <div className={styles.wrapper}>
                <p className={styles.subtitle}>
                  We were not able to verify your shipping address, please
                  choose one of the options below.
                </p>
                <p className={`${styles.highlighted} ${styles.suggestion}`}>
                  We have highlighted our suggestions in green.
                </p>
              </div>
              <div className={styles.options}>
                <AddressOption
                  title="Suggested address"
                  data={verified}
                  suggestedFields={suggestedFields}
                  isSelected={suggestedSelected}
                  onClick={toggleSelected}
                />
                <AddressOption
                  title="Address as entered"
                  data={submitted}
                  isSelected={!suggestedSelected}
                  onClick={toggleSelected}
                />
              </div>
              <Button
                onClick={handleClickUseThisAddress}
                buttonType={BUTTON_TYPES.TERTIARY}
                className={styles.button}
                disabled={loading}
              >
                Use this Address
              </Button>
            </>
          )}
          {modal === 'undeliverable' && (
            <>
              <h1 className={styles.title}>
                Oops! 
                {' '}
                <span>Please Verify</span>
              </h1>
              <p className={styles.subtitle}>
                The address you entered is not deliverable.
                <span>Please edit your address below.</span>
              </p>
              <div className={styles.address}>
                <p>{formatAddress(submitted.primaryLine)}</p>
                {submitted.secondaryLine && (
                  <p>{formatAddress(submitted.secondaryLine)}</p>
                )}
                <p>
                  {`${formatAddress(submitted.city)}, ${submitted.state ||
                  ''} ${submitted.zip}`}

                </p>
                <p>
                  {get(
                    find(COUNTRY_SELECTION, ['code', submitted.countryCode]),
                    'name'
                  )}
                </p>
              </div>
              <Button
                onClick={handleClickClose}
                buttonType={BUTTON_TYPES.TERTIARY}
                className={styles.button}
              >
                Edit Address
              </Button>
              <button
                type="button"
                className={styles.link}
                onClick={onClickUseAsEntered}
                disabled={loading}
              >
                Use address as entered
              </button>
            </>
          )}
        </div>
      </div>
    </BaseModal>
  );
};

VerificationModal.propTypes = {
  modal: PropTypes.string,
  data: PropTypes.shape({
    submitted: PropTypes.shape(),
    verified: PropTypes.shape(),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onClickUseThisAddress: PropTypes.func.isRequired,
  onClickUseAsEntered: PropTypes.func.isRequired,
};

VerificationModal.defaultProps = {
  modal: null,
};

export default VerificationModal;
